<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { Icon } from '@iconify/vue'
import ConditionModal from './ConditionModal.vue'

// ---- Types ----
interface ConditionClause {
  key: string
  op: string
  value: unknown
}

type AudienceNode =
  | { type: 'GROUP_AND' | 'GROUP_OR'; child: AudienceNode[] }
  | { type: 'CONDITION'; child: ConditionClause[] }

interface ConditionTag {
  id: number
  label: string
  clauses: ConditionClause[]
}

interface ConditionGroup {
  id: number
  operator: 'AND' | 'OR'
  conditions: ConditionTag[]
}

const props = defineProps<{ modelValue?: AudienceNode | null }>()
const emit = defineEmits<{
  'update:modelValue': [value: AudienceNode | null]
}>()

// ---- State ----
const outerOperator = ref<'AND' | 'OR'>('OR')
const groups = ref<ConditionGroup[]>([])
const outerConditions = ref<ConditionTag[]>([])

let nextId = 1

function resetIds() {
  nextId = 1
}

function buildLabelFromClauses(clauses: ConditionClause[]) {
  const genderMap: Record<string, string> = { male: 'Nam', female: 'Nữ', other: 'Khác' }
  const kycMap: Record<string, string> = {
    not_kyc: 'Chưa KYC',
    pending: 'Đang xử lý',
    verified: 'Đã KYC',
  }
  const deviceMap: Record<string, string> = {
    ios: 'người dùng iOS',
    android: 'người dùng Android',
    all: 'Tất cả thiết bị',
  }
  const lastActiveMap: Record<string, string> = {
    gt_30: '> 30 ngày',
    gt_7: '> 7 ngày',
    lt_7: 'Trong 7 ngày',
  }
  const accountTypeMap: Record<string, string> = {
    all: 'Tất cả loại tài khoản',
    broker: 'Tất cả môi giới',
    customer: 'Khách',
    direct_superior: 'Cấp trên trực tiếp',
    system_director: 'Giám đốc hệ thống',
  }

  const quote = (v: unknown) => `“${String(v ?? '')}”`
  const parts: string[] = []

  for (const c of clauses.filter((x) => x?.key && x?.op)) {
    if (c.key === 'name' && c.op === 'include') {
      parts.push(`Từ khóa chứa: ${quote(c.value)}`)
      continue
    }

    if (c.key === 'age' && c.op === 'between') {
      const raw = String(c.value ?? '')
      const [min, max] = raw.split('|')
      if (min && max) parts.push(`Tuổi >${min} AND tuổi < ${max}`)
      else parts.push(`Tuổi: ${raw}`)
      continue
    }

    if (c.key === 'gender' && c.op === 'eq') {
      parts.push(`Giới tính: ${genderMap[String(c.value)] ?? String(c.value ?? '')}`)
      continue
    }

    if (c.key === 'kyc_status' && c.op === 'eq') {
      parts.push(`KYC: ${kycMap[String(c.value)] ?? String(c.value ?? '')}`)
      continue
    }

    if (c.key === 'device_type' && c.op === 'eq') {
      parts.push(`Thiết bị: ${deviceMap[String(c.value)] ?? String(c.value ?? '')}`)
      continue
    }

    if (c.key === 'last_active' && c.op === 'eq') {
      parts.push(`Hoạt động: ${lastActiveMap[String(c.value)] ?? String(c.value ?? '')}`)
      continue
    }

    if (c.key === 'account_type' && c.op === 'in') {
      const values = Array.isArray(c.value) ? c.value : []
      const labels = values.map((v) => accountTypeMap[String(v)] ?? String(v))
      parts.push(`Tài khoản: ${labels.join(', ')}`)
      continue
    }

    if (c.key === 'register_date') {
      if (c.op === 'between') {
        const raw = String(c.value ?? '')
        const [from, to] = raw.split('|')
        parts.push(`Ngày ĐK: ${from ?? ''} → ${to ?? ''}`.trim())
        continue
      }
      if (c.op === 'gte') {
        parts.push(`Ngày ĐK: từ ${String(c.value ?? '')}`)
        continue
      }
      if (c.op === 'lte') {
        parts.push(`Ngày ĐK: đến ${String(c.value ?? '')}`)
        continue
      }
    }

    if (c.key === 'birthday_day' && c.op === 'eq') {
      parts.push(`Ngày sinh (ngày): ${String(c.value ?? '')}`)
      continue
    }
    if (c.key === 'birthday_month' && c.op === 'eq') {
      parts.push(`Ngày sinh (tháng): ${String(c.value ?? '')}`)
      continue
    }

    if (c.key === 'regions' && c.op === 'in') {
      const n = Array.isArray(c.value) ? c.value.length : 0
      parts.push(n ? `Khu vực: ${n} khu vực` : 'Khu vực')
      continue
    }

    if (c.key === 'account_id' && c.op === 'in') {
      const n = Array.isArray(c.value) ? c.value.length : 0
      parts.push(n ? `Chính xác TK: ${n} tài khoản` : 'Chính xác TK')
      continue
    }

    // fallback (avoid showing raw key-only if possible, but keep something readable)
    const v = Array.isArray(c.value) ? `[${c.value.length}]` : String(c.value ?? '')
    parts.push(`${c.key} ${c.op} ${v}`.trim())
  }

  return parts.join(' | ') || 'Điều kiện'
}

function hydrateFromModel(value: AudienceNode | null | undefined) {
  // clear UI
  outerConditions.value = []
  groups.value = []
  outerOperator.value = 'OR'
  resetIds()

  if (!value) return

  if (value.type === 'GROUP_AND') outerOperator.value = 'AND'
  if (value.type === 'GROUP_OR') outerOperator.value = 'OR'

  if (value.type === 'CONDITION') {
    const clauses = value.child || []
    if (clauses.length) {
      outerConditions.value.push({
        id: nextId++,
        label: buildLabelFromClauses(clauses),
        clauses,
      })
    }
    return
  }

  // root GROUP_*: split into groups + outer CONDITIONs
  for (const child of value.child || []) {
    if (!child) continue

    if (child.type === 'CONDITION') {
      const clauses = child.child || []
      if (!clauses.length) continue
      outerConditions.value.push({
        id: nextId++,
        label: buildLabelFromClauses(clauses),
        clauses,
      })
      continue
    }

    if (child.type === 'GROUP_AND' || child.type === 'GROUP_OR') {
      const conditionNodes = (child.child || []).filter((n) => n?.type === 'CONDITION') as Array<
        Extract<AudienceNode, { type: 'CONDITION' }>
      >
      const tags: ConditionTag[] = conditionNodes
        .map((n) => n.child || [])
        .filter((clauses) => clauses.filter((c) => c?.key && c?.op).length > 0)
        .map((clauses) => ({
          id: nextId++,
          label: buildLabelFromClauses(clauses),
          clauses: clauses.filter((c) => c?.key && c?.op),
        }))
      if (!tags.length) continue
      groups.value.push({
        id: nextId++,
        operator: child.type === 'GROUP_AND' ? 'AND' : 'OR',
        conditions: tags,
      })
    }
  }
}

// Modal state — tracks which level is requesting a condition
// level: 'outer' | groupId (number)
const modalVisible = ref(false)
const modalTarget = ref<'outer' | number>('outer')
const modalInitialClauses = ref<ConditionClause[] | null>(null)
const editingTag = ref<null | { target: 'outer' | number; tagId: number }>(null)

const operatorOptions: Array<'AND' | 'OR'> = ['OR', 'AND']

// ---- Actions ----
function addGroup() {
  groups.value.push({
    id: nextId++,
    operator: 'AND',
    conditions: [],
  })
}

function removeGroup(groupId: number) {
  groups.value = groups.value.filter((g) => g.id !== groupId)
}

function openModal(target: 'outer' | number) {
  modalTarget.value = target
  modalInitialClauses.value = null
  editingTag.value = null
  modalVisible.value = true
}

function handleConfirm(payload: { label: string; clauses: ConditionClause[] }) {
  console.log('[push-campaign][ConditionBuilder] modal payload', payload)
  const tag: ConditionTag = {
    id: nextId++,
    label: payload.label || buildLabelFromClauses(payload.clauses || []),
    clauses: (payload.clauses || []).filter((c) => c?.key && c?.op),
  }

  if (editingTag.value) {
    const { target, tagId } = editingTag.value
    if (target === 'outer') {
      outerConditions.value = outerConditions.value.filter((t) => t.id !== tagId)
    } else {
      const group = groups.value.find((g) => g.id === target)
      if (group) group.conditions = group.conditions.filter((t) => t.id !== tagId)
    }
  }

  if (modalTarget.value === 'outer') {
    outerConditions.value.push(tag)
  } else {
    const group = groups.value.find((g) => g.id === modalTarget.value)
    if (group) group.conditions.push(tag)
  }

  modalVisible.value = false
}

function openModalForTag(target: 'outer' | number, tag: ConditionTag) {
  modalTarget.value = target
  modalInitialClauses.value = tag.clauses
  editingTag.value = { target, tagId: tag.id }
  modalVisible.value = true
}

function removeOuterCondition(id: number) {
  outerConditions.value = outerConditions.value.filter((c) => c.id !== id)
}

function removeGroupCondition(groupId: number, conditionId: number) {
  const group = groups.value.find((g) => g.id === groupId)
  if (group) {
    group.conditions = group.conditions.filter((c) => c.id !== conditionId)
  }
}

function buildPayload(): AudienceNode | null {
  const children: AudienceNode[] = []

  // groups -> GROUP_(AND|OR)
  for (const g of groups.value) {
    const conditionNodes: AudienceNode[] = g.conditions
      .map((t) => ({ type: 'CONDITION', child: t.clauses.filter((c) => c?.key && c?.op) }) as AudienceNode)
      .filter((n) => (n.type === 'CONDITION' ? (n.child?.length || 0) > 0 : true))

    if (!conditionNodes.length) continue
    children.push({
      type: g.operator === 'AND' ? 'GROUP_AND' : 'GROUP_OR',
      child: conditionNodes,
    })
  }

  // outer conditions -> many CONDITION nodes (keep separated)
  for (const t of outerConditions.value) {
    const clauses = t.clauses.filter((c) => c?.key && c?.op)
    if (!clauses.length) continue
    children.push({ type: 'CONDITION', child: clauses })
  }

  if (!children.length) return null

  return {
    type: outerOperator.value === 'AND' ? 'GROUP_AND' : 'GROUP_OR',
    child: children,
  }
}

const apiPayload = computed(() => buildPayload())

function emitChange() {
  console.log('[push-campaign][ConditionBuilder] emit payload', apiPayload.value)
  emit('update:modelValue', apiPayload.value)
}

watch(
  () => props.modelValue,
  (val) => {
    hydrateFromModel(val ?? null)
  },
  { immediate: true },
)
</script>

<template>
  <div class="flex gap-4 items-start w-full">
    <!-- Outer operator dropdown (OR / AND) -->
    <div class="shrink-0 relative">
      <select
        v-model="outerOperator"
        @change="emitChange"
        class="bg-[#f7f7f7] border-none rounded-[4px] px-3 py-[14px] text-[#777] text-[16px] cursor-pointer outline-none appearance-none pr-8"
      >
        <option v-for="op in operatorOptions" :key="op" :value="op">{{ op }}</option>
      </select>
      <Icon
        icon="prime:chevron-down"
        class="absolute right-2 top-1/2 -translate-y-1/2 text-[#777] pointer-events-none"
        width="16"
      />
    </div>

    <!-- Outer dashed container -->
    <div
      class="flex-1 border border-dashed border-[#cdcdcd] rounded-[4px] p-4 flex flex-col gap-[10px]"
    >
      <!-- Toolbar: Add group + Add condition -->
      <div class="flex gap-[22px] items-center">
        <button
          class="flex gap-[7px] items-center rounded-[4px] cursor-pointer hover:opacity-80"
          @click="addGroup"
        >
          <Icon icon="streamline-cyber:filter-plus" width="16" color="#0035ba" />
          <span class="font-bold text-[#0035ba] text-[10px]">Thêm nhóm điều kiện</span>
        </button>

        <button
          class="flex gap-[7px] items-center rounded-[4px] cursor-pointer hover:opacity-80"
          @click="openModal('outer')"
        >
          <Icon icon="fluent:filter-add-20-regular" width="16" color="#0035ba" />
          <span class="font-bold text-[#0035ba] text-[10px]">Thêm điều kiện</span>
        </button>
      </div>

      <!-- Condition groups (nested) -->
      <div v-for="group in groups" :key="group.id" class="flex gap-4 items-start w-full">
        <!-- Inner operator dropdown -->
        <div class="shrink-0 relative">
          <select
            v-model="group.operator"
            @change="emitChange"
            class="bg-[#f7f7f7] border-none rounded-[4px] px-3 py-[14px] text-[#777] text-[16px] cursor-pointer outline-none appearance-none pr-8"
          >
            <option v-for="op in operatorOptions" :key="op" :value="op">{{ op }}</option>
          </select>
          <Icon
            icon="prime:chevron-down"
            class="absolute right-2 top-1/2 -translate-y-1/2 text-[#777] pointer-events-none"
            width="16"
          />
        </div>

        <!-- Inner dashed container -->
        <div
          class="relative flex-1 border border-dashed border-[#cdcdcd] rounded-[4px] p-4 flex flex-col gap-[10px]"
        >
          <!-- Remove group button -->
          <button
            class="absolute top-[-6px] right-[-6px] cursor-pointer hover:opacity-80"
            @click="
              () => {
                removeGroup(group.id)
                emitChange()
              }
            "
          >
            <Icon icon="carbon:close-filled" width="16" color="#e53935" />
          </button>

          <!-- Add condition button (inner) -->
          <div class="flex gap-[22px] items-center">
            <button
              class="flex gap-[7px] items-center rounded-[4px] cursor-pointer hover:opacity-80"
              @click="openModal(group.id)"
            >
              <Icon icon="fluent:filter-add-20-regular" width="16" color="#0035ba" />
              <span class="font-bold text-[#0035ba] text-[10px]">Thêm điều kiện</span>
            </button>
          </div>

          <!-- Inner condition tags: nhiều nhãn cùng hàng (wrap khi hết chỗ), mỗi nhãn vẫn ellipsis một dòng như cũ -->
          <div class="flex flex-wrap gap-[10px] items-start w-full min-w-0">
            <div
              v-for="condition in group.conditions"
              :key="condition.id"
              class="relative inline-flex gap-[10px] items-center bg-[#daeeff] px-4 py-[7px] rounded-[4px] self-start max-w-full"
            >
              <span
                class="text-[#414141] text-[12px] whitespace-nowrap overflow-hidden text-ellipsis max-w-[300px] cursor-pointer hover:opacity-80"
                @click="openModalForTag(group.id, condition)"
              >
                {{ condition.label }}
              </span>
              <button
                class="absolute top-[-6px] right-[-6px] cursor-pointer hover:opacity-80"
                @click="
                  () => {
                    removeGroupCondition(group.id, condition.id)
                    emitChange()
                  }
                "
              >
                <Icon icon="carbon:close-filled" width="16" color="#e53935" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Outer condition tags: nhiều nhãn cùng hàng (wrap khi hết chỗ), mỗi nhãn vẫn ellipsis một dòng như cũ -->
      <div class="flex flex-wrap gap-[10px] items-start w-full min-w-0">
        <div
          v-for="condition in outerConditions"
          :key="condition.id"
          class="relative inline-flex gap-[10px] items-center bg-[#daeeff] px-4 py-[7px] rounded-[4px] self-start max-w-full"
        >
          <span
            class="text-[#414141] text-[12px] whitespace-nowrap overflow-hidden text-ellipsis max-w-[500px] cursor-pointer hover:opacity-80"
            @click="openModalForTag('outer', condition)"
          >
            {{ condition.label }}
          </span>
          <button
            class="absolute top-[-6px] right-[-6px] cursor-pointer hover:opacity-80"
            @click="
              () => {
                removeOuterCondition(condition.id)
                emitChange()
              }
            "
          >
            <Icon icon="carbon:close-filled" width="16" color="#e53935" />
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Modal -->
  <ConditionModal
    :show="modalVisible"
    :initialClauses="modalInitialClauses"
    @on:close="modalVisible = false"
    @on:confirm="
      (payload) => {
        handleConfirm(payload)
        emitChange()
      }
    "
  />
</template>
